import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { BlogPostDto, BlogTagDto, Notification } from '../types';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { isPlatformSystemAdmin } from '../utils/roleUtils';
import { resolveBlogFeaturedImageSrc } from '../utils/blogImage';
import SeoHead from '../components/SeoHead';
import {
  ORGANIZATION_JSON_LD,
  articleSeoDescription,
  articleSeoTitle,
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  publicPath,
  softwareApplicationJsonLd,
} from '../utils/publicSite';
import './BlogProse.css';

function formatBlogDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

const articleAdminEditBtnClass =
  'inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors bg-[#eef2ff] text-[#0B4778] border-[#cde0ff] hover:bg-[#dcecff] shrink-0';

const articleCardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '18px',
  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
};

const tagTone = (tone: string): React.CSSProperties => {
  switch (tone) {
    case 'green':
      return { background: '#e8f8ef', color: '#0f7c38' };
    case 'amber':
      return { background: '#fff7df', color: '#996b00' };
    case 'orange':
      return { background: '#fff1e8', color: '#bd3f00' };
    default:
      return { background: '#eaf2ff', color: '#0B4778' };
  }
};

const ArticleHeader: React.FC<{ post: BlogPostDto }> = ({ post }) => (
  <>
    <div className="flex flex-wrap gap-2 text-xs mb-2" style={{ color: '#64748b' }}>
      <span>{post.badge || 'Article'}</span>
      <span aria-hidden="true">•</span>
      <span>{formatBlogDate(post.publishedAt)}</span>
      <span aria-hidden="true">•</span>
      <span>{post.readTimeMinutes ?? 1} min read</span>
    </div>
    <h1 className="mb-3" style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
      {post.title}
    </h1>
    <div className="flex flex-wrap gap-1 mb-2">
      {(post.tags || []).map((t: BlogTagDto) => (
        <span
          key={t.label}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
          style={tagTone(t.tone)}
        >
          {t.label}
        </span>
      ))}
    </div>
  </>
);

const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [post, setPost] = useState<BlogPostDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const canAuthor = Boolean(user && isPlatformSystemAdmin(user));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((n: Notification) => !n.is_read));
      } catch {
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await apiService.getBlogPostBySlug(slug);
        if (!cancelled) {
          setPost(data);
          setNotFound(false);
        }
      } catch {
        if (!cancelled) {
          setPost(null);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!slug) {
    return <Navigate to="/blog/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex justify-center items-center py-24">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return <Navigate to="/blog/" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
      <SeoHead
        title={articleSeoTitle(post)}
        description={articleSeoDescription(post)}
        canonicalPath={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.featuredImageUrl}
        jsonLd={[
          ORGANIZATION_JSON_LD,
          softwareApplicationJsonLd(),
          buildArticleJsonLd(post),
          buildBreadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink
        showLogoutButton
        showAuthButtons
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <main className="flex-1 py-4">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 w-full">
          <nav className="mb-3 flex flex-wrap items-center gap-2 text-sm" aria-label="Breadcrumb" style={{ color: '#64748b' }}>
            <Link to={publicPath('/')} className="font-medium text-[#0B4778] hover:underline">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to={publicPath('/blog')} className="font-medium text-[#0B4778] hover:underline">Blog</Link>
            <span aria-hidden="true">/</span>
            <span className="text-gray-700">{post.title}</span>
          </nav>

          <article className="p-4 md:p-6" style={articleCardStyle}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <ArticleHeader post={post} />
              </div>
              {canAuthor && (
                <Link
                  to={`/blog/edit/${post.slug}`}
                  className={articleAdminEditBtnClass}
                >
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                  Edit
                </Link>
              )}
            </div>
            {(() => {
              const featuredSrc = resolveBlogFeaturedImageSrc(post);
              return featuredSrc ? (
                <img
                  src={featuredSrc}
                  alt=""
                  className="w-full h-auto rounded-xl mb-4 object-contain"
                />
              ) : null;
            })()}
            <div
              className="blog-article-body blog-prose max-w-none"
              dangerouslySetInnerHTML={{ __html: post.bodyHtml || '' }}
            />
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
